import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AuthNavbar from "components/AuthNavbar";
import LanguageSelector from "../components/LanguageSelector";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getmessage } from "utils/i18messages";
import CustomSnackbar from "components/CustomSnackbar";
import DotPulseLoading from "components/Loading/DotPulseLoading";

const EyeIcon = ({ show }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    {show ? (
      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
    ) : (
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    )}
  </svg>
);

// Password strength calculator
const calculatePasswordStrength = (password) => {
  let strength = 0;
  const checks = {
    hasLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[@$!%*?&#]/.test(password),
  };

  if (checks.hasLength) strength += 20;
  if (checks.hasUpperCase) strength += 20;
  if (checks.hasLowerCase) strength += 20;
  if (checks.hasNumber) strength += 20;
  if (checks.hasSpecial) strength += 20;

  return {
    strength: Math.min(strength, 100),
    checks,
  };
};

const PasswordStrengthIndicator = ({ strength, checks, t }) => {
  const strengthLabel =
    strength < 40
      ? t("password_strength.weak")
      : strength < 60
      ? t("password_strength.medium")
      : strength < 80
      ? t("password_strength.strong")
      : t("password_strength.very_strong");

  const strengthColor =
    strength < 40
      ? "#ef5350" // red
      : strength < 60
      ? "#fb8c00" // orange
      : strength < 80
      ? "#fdd835" // yellow
      : "#66bb6a"; // green

  const criteria = [
    { label: t("password_strength.8_chars"), met: checks.hasLength },
    { label: t("password_strength.uppercase"), met: checks.hasUpperCase },
    { label: t("password_strength.lowercase"), met: checks.hasLowerCase },
    { label: t("password_strength.number"), met: checks.hasNumber },
    { label: t("password_strength.special_char"), met: checks.hasSpecial },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
      animate={{
        opacity: 1,
        height: "auto",
        marginTop: "-10px",
        marginBottom: "24px",
      }}
      exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ overflow: "hidden" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
          paddingTop: "10px",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "8px",
            backgroundColor: "#e0e0e0",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <motion.div
            style={{
              height: "100%",
              backgroundColor: strengthColor,
              borderRadius: "4px",
            }}
            initial={{ width: "0%" }}
            animate={{ width: `${strength}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>
        <span
          style={{
            marginLeft: "12px",
            fontSize: "14px",
            fontWeight: "600",
            color: strengthColor,
          }}
        >
          {strengthLabel}
        </span>
      </div>
      <div style={{ fontSize: "13px", color: "#718096" }}>
        <ul
          style={{
            paddingLeft: "0px",
            marginTop: "8px",
            listStyleType: "none",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4px 8px",
          }}
        >
          {criteria.map((item, index) => (
            <motion.li
              key={index}
              style={{
                color: item.met ? "#66bb6a" : "#718096",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={item.met ? 3 : 2}
              >
                {item.met ? (
                  <path d="M20 6L9 17l-5-5" />
                ) : (
                  <path d="M18 6L6 18M6 6l12 12" />
                )}
              </svg>
              {item.label}
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 968);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const passwordStrength = calculatePasswordStrength(password);

  useEffect(() => {
    if (!token) {
      navigate("/");
    }
    const handleResize = () => setIsMobile(window.innerWidth < 968);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [token, navigate]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "en";
    if (password !== confirmPassword) {
      return setSnackbar({
        open: true,
        message: getmessage("profile_settings.password_mismatch"),
        severity: "error",
      });
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

    if (!passwordRegex.test(password)) {
      setSnackbar({
        open: true,
        message: getmessage("profile_settings.password_invalid"),
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/reset-password`,
        { token, password },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );

      setSnackbar({
        open: true,
        message: response.data.message || "Password updated successfully",
        severity: "success",
      });

      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Reset failed",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthNavbar />
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#ffffff",
          display: "flex",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(15px, -15px); }
        }
      `}</style>

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DotPulseLoading size={14} color="#c9846b" />
          </div>
        )}

        {/* Abstract Bubble Background - Left Side (Red Theme) */}
        <div
          style={{
            position: "absolute",
            left: "-25%",
            top: "40%",
            transform: "translateY(-50%)",
            width: isMobile ? "150%" : "85%",
            height: "150%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 40px rgba(211, 47, 47, 0.15))",
            }}
          >
            <defs>
              <linearGradient
                id="gradientReset"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#ffebee", stopOpacity: 1 }}
                />
                <stop
                  offset="50%"
                  style={{ stopColor: "#ffcdd2", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#ef9a9a", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>
            <path
              d="M 150,100 C 150,50 200,20 280,50 C 360,80 400,120 450,200 C 500,280 520,360 480,450 C 440,540 400,600 350,680 C 300,760 250,820 180,860 C 110,900 50,880 30,800 C 10,720 -20,640 20,540 C 60,440 100,360 120,260 C 140,160 150,150 150,100 Z"
              fill="url(#gradientReset)"
              style={{
                animation: "float 8s ease-in-out infinite",
              }}
            />
          </svg>

          {/* Smaller floating bubbles */}
          <div
            style={{
              position: "absolute",
              top: "15%",
              right: "10%",
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(239, 154, 154, 0.4), rgba(211, 47, 47, 0.2))",
              animation: "floatSlow 6s ease-in-out infinite",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: "20%",
              right: "25%",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(255, 205, 210, 0.5), rgba(239, 154, 154, 0.3))",
              animation: "floatSlow 7s ease-in-out infinite reverse",
            }}
          />

          <div
            style={{
              position: "absolute",
              top: "45%",
              right: "5%",
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(255, 235, 238, 0.6), rgba(255, 205, 210, 0.4))",
              animation: "float 5s ease-in-out infinite",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: "20%",
              right: "10%",
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(239, 154, 154, 0.5), rgba(211, 47, 47, 0.3))",
              animation: "floatSlow 6s ease-in-out infinite reverse",
            }}
          />
        </div>

        {/* Main Large Bubble - Right Bottom */}
        <div
          style={{
            position: "absolute",
            right: "-25%",
            bottom: "10%",
            width: isMobile ? "150%" : "85%",
            height: "150%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 40px rgba(211, 47, 47, 0.15))",
              opacity: 0.2,
            }}
          >
            <defs>
              <linearGradient
                id="gradientReset2"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#ffebee", stopOpacity: 1 }}
                />
                <stop
                  offset="50%"
                  style={{ stopColor: "#ffcdd2", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#ef9a9a", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>

            <path
              d="M 150,100 C 150,50 200,20 280,50 C 360,80 900,120 450,200 C 500,280 520,360 480,450 C 440,540 400,600 350,680 C 300,760 250,820 180,860 C 110,900 50,880 30,800 C 10,720 -20,640 20,540 C 60,440 100,360 120,260 C 140,160 150,150 150,100 Z"
              fill="url(#gradientReset2)"
              style={{
                animation: "float 8s ease-in-out infinite",
              }}
            />
          </svg>
        </div>

        {/* Right Side - Reset Form */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "32px 24px" : "48px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Reset Form Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            style={{
              width: "100%",
              maxWidth: "480px",
              marginTop: "0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
                position: "relative", // Ensure z-index works
                zIndex: 20, // Higher than AuthNavbar's zIndex
              }}
            >
              <LanguageSelector />
            </div>

            <h1
              style={{
                fontSize: isMobile ? "32px" : "40px",
                fontWeight: "bold",
                color: "#1a202c",
                marginBottom: "12px",
                textAlign: "center",
              }}
            >
              {t("resetPassword.title")}
            </h1>

            <p
              style={{
                fontSize: "16px",
                color: "#718096",
                textAlign: "center",
                marginBottom: "40px",
              }}
            >
              {t("resetPassword.subtitle")}
            </p>

            <form onSubmit={handleSubmit}>
              {/* Password Input */}
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4a5568",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("resetPassword.passwordLabel")}
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••"
                    style={{
                      width: "100%",
                      padding: "14px 48px 14px 16px",
                      fontSize: "15px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      transition: "all 0.3s ease",
                      outline: "none",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#d32f2f";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(211, 47, 47, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#d1d5db";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#718096",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <EyeIcon show={showPassword} />
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {password && (
                  <PasswordStrengthIndicator
                    strength={passwordStrength.strength}
                    checks={passwordStrength.checks}
                    t={t}
                  />
                )}
              </AnimatePresence>

              {/* Confirm Password Input */}
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4a5568",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("resetPassword.confirmPasswordLabel")}
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="••••••"
                    style={{
                      width: "100%",
                      padding: "14px 48px 14px 16px",
                      fontSize: "15px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      transition: "all 0.3s ease",
                      outline: "none",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#d32f2f";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(211, 47, 47, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#d1d5db";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#718096",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <EyeIcon show={showConfirmPassword} />
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={handleSubmit}
                style={{
                  width: "100%",
                  padding: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "white",
                  backgroundColor: "#d32f2f",
                  border: "none",
                  borderRadius: "8px",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                  marginBottom: "24px",
                  fontFamily: "inherit",
                  transition: "all 0.3s ease",
                }}
              >
                {loading ? "Resetting..." : t("resetPassword.resetButton")}
              </motion.button>

              {/* Back to Login Link */}
              <div style={{ textAlign: "center" }}>
                <a
                  href="/login"
                  style={{
                    color: "#d32f2f",
                    textDecoration: "none",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Back to Login
                </a>
              </div>
            </form>
          </motion.div>
        </div>

        {/* Snackbar */}
        <CustomSnackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </div>
    </>
  );
}

export default ResetPassword;
